import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../utils/api';
import CalendarForm from './CalendarForm';

export default function CalendarTab() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const [editData, setEditData] = useState({});

  const loadEvents = useCallback(async () => {
    try {
      setLoading(true);
      // Fetch calendar events
      const calendarData = await apiCall('/api/calendar');

      // Fetch tasks with due dates
      let tasksWithDates = [];
      try {
        const allTasks = await apiCall('/api/tasks');
        tasksWithDates = allTasks
          .filter(task => task.due_date)
          .map(task => ({
            id: `task-${task.id}`,
            title: `Task: ${task.title}`,
            description: task.description || `Assigned to: ${task.assigned_to || 'team'}`,
            date: task.due_date,
            type: 'task',
            status: task.status,
            originalTask: task,
          }));
      } catch (taskErr) {
        console.error('Error loading tasks:', taskErr);
        // Continue without tasks if there's an error
      }

      // Combine and sort events
      const allEvents = [...calendarData, ...tasksWithDates].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
      setEvents(allEvents);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const handleEventCreated = (newEvent) => {
    setEvents([...events, newEvent].sort((a, b) => new Date(a.date) - new Date(b.date)));
    setShowForm(false);
  };

  const handleDelete = async (eventId) => {
    if (!confirm('Delete this event?')) return;
    try {
      // Check if it's a task (id starts with 'task-')
      if (eventId.startsWith('task-')) {
        const taskId = eventId.replace('task-', '');
        await apiCall(`/api/tasks/${taskId}`, { method: 'DELETE' });
      } else {
        await apiCall(`/api/calendar/${eventId}`, { method: 'DELETE' });
      }
      setEvents(events.filter((e) => e.id !== eventId));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEditStart = (event) => {
    setEditingEventId(event.id);
    setEditData({
      title: event.title,
      description: event.description || '',
      date: event.date,
      type: event.type,
      minutes: event.minutes || '',
    });
  };

  const handleEditSave = async () => {
    try {
      await apiCall(`/api/calendar/${editingEventId}`, {
        method: 'PUT',
        body: JSON.stringify(editData),
      });
      setEvents(
        events.map((e) =>
          e.id === editingEventId
            ? {
                ...e,
                title: editData.title,
                description: editData.description,
                date: editData.date,
                type: editData.type,
                minutes: editData.minutes,
              }
            : e
        )
      );
      setEditingEventId(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getEventColor = (type, status) => {
    if (type === 'task') {
      if (status === 'done') return 'border-green-500 bg-green-50';
      if (status === 'in-progress') return 'border-blue-500 bg-blue-50';
      return 'border-orange-500 bg-orange-50';
    }
    switch (type) {
      case 'milestone':
        return 'border-secondary-fixed bg-secondary-fixed bg-opacity-10';
      case 'decision':
        return 'border-tertiary-fixed bg-tertiary-fixed bg-opacity-10';
      case 'meeting':
        return 'border-primary-fixed bg-primary-fixed bg-opacity-10';
      default:
        return 'border-outline-variant bg-surface-container-low';
    }
  };

  if (loading) return <div className="text-center py-12 text-on-surface">Loading calendar...</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
        <h2 className="text-xl sm:text-2xl font-bold font-headline">Key Dates & Milestones</h2>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary text-sm sm:text-base">
          {showForm ? 'Cancel' : '+ Add date'}
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && <CalendarForm onEventCreated={handleEventCreated} />}

      <div className="space-y-3">
        {events.length === 0 ? (
          <div className="card text-center py-6 text-on-surface-variant">
            <p className="text-sm">No events yet. Add milestones, decisions, and meetings.</p>
          </div>
        ) : (
          events.map((event) => (
            <div key={event.id} className={`card p-3 sm:p-4 border-l-4 ${getEventColor(event.type, event.status)}`}>
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-sm sm:text-base break-words">{event.title}</h3>
                  <div className="flex flex-wrap gap-1 mt-2">
                    <span className="badge text-xs inline-block">{event.type}</span>
                    {event.status && event.type === 'task' && (
                      <span className="text-xs px-2 py-1 rounded bg-surface-container text-on-surface-variant">
                        {event.status}
                      </span>
                    )}
                  </div>
                  {event.description && (
                    <p className="text-xs sm:text-sm text-on-surface-variant mt-2 line-clamp-2 break-words">{event.description}</p>
                  )}
                </div>
              </div>
              {event.type === 'meeting' && (
                <div className="mt-2 p-2 rounded bg-surface-container-low">
                  <p className="text-xs font-semibold text-on-surface-variant mb-1">📝 Minutes</p>
                  {event.minutes ? (
                    <p className="text-xs sm:text-sm text-on-surface whitespace-pre-wrap break-words">{event.minutes}</p>
                  ) : (
                    <p className="text-xs text-on-surface-variant italic">No minutes added yet</p>
                  )}
                </div>
              )}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mt-2">
                <p className="text-xs text-on-surface-variant">{formatDate(event.date)}</p>
                <div className="flex gap-2 w-full sm:w-auto">
                  {!event.id.startsWith('task-') && event.type === 'meeting' && (
                    <button onClick={() => handleEditStart(event)} className="btn-secondary text-xs flex-1 sm:flex-none">
                      {event.minutes ? 'Edit minutes' : '📝 Add minutes'}
                    </button>
                  )}
                  {!event.id.startsWith('task-') && (
                    <button onClick={() => handleEditStart(event)} className="btn-primary text-xs flex-1 sm:flex-none">
                      Edit
                    </button>
                  )}
                  <button onClick={() => handleDelete(event.id)} className="btn-danger text-xs flex-1 sm:flex-none">
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {editingEventId && (
        <EventEditModal
          event={editData}
          onSave={handleEditSave}
          onCancel={() => setEditingEventId(null)}
          onChange={setEditData}
        />
      )}
    </div>
  );
}

function EventEditModal({ event, onSave, onCancel, onChange }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-surface-container-lowest rounded-lg sm:rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between p-3 sm:p-6 border-b border-outline-variant gap-3 bg-surface-container-lowest">
          <h2 className="font-bold font-headline text-sm sm:text-lg">Edit Event</h2>
          <button
            onClick={onCancel}
            className="text-on-surface-variant hover:text-on-surface font-bold text-2xl flex-shrink-0"
          >
            ×
          </button>
        </div>

        <div className="p-3 sm:p-6 space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium mb-2">Title *</label>
            <input
              type="text"
              value={event.title}
              onChange={(e) => onChange({ ...event, title: e.target.value })}
              className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-medium mb-2">Description</label>
            <textarea
              value={event.description}
              onChange={(e) => onChange({ ...event, description: e.target.value })}
              rows="3"
              className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2">Date</label>
              <input
                type="date"
                value={event.date}
                onChange={(e) => onChange({ ...event, date: e.target.value })}
                className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2">Type</label>
              <select
                value={event.type}
                onChange={(e) => onChange({ ...event, type: e.target.value })}
                className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="milestone">Milestone</option>
                <option value="decision">Decision</option>
                <option value="meeting">Meeting</option>
              </select>
            </div>
          </div>

          {event.type === 'meeting' && (
            <div>
              <label className="block text-xs sm:text-sm font-medium mb-2">📝 Minutes</label>
              <textarea
                value={event.minutes || ''}
                onChange={(e) => onChange({ ...event, minutes: e.target.value })}
                placeholder="What was discussed, decided, and actioned in this meeting..."
                rows="6"
                className="w-full px-3 sm:px-4 py-2 text-sm rounded-lg border border-outline-variant bg-surface text-on-surface focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-2 pt-4">
            <button onClick={onSave} className="btn-primary flex-1 text-sm">
              Save Changes
            </button>
            <button onClick={onCancel} className="btn-secondary flex-1 sm:flex-none text-sm">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
